import React from "react";
import AgoraRTM from "agora-rtm-sdk";
import type { RTMClient, RTMEvents } from "agora-rtm-sdk";
import { v4 as uuidv4 } from "uuid";

import { ChatStatus } from "../providers/ChatProvider";
import type {
	BanUserResponse,
	Message,
	ModerationMessage,
	PinMessage,
	TextMessage,
	UserMessage,
} from "../types";
import {
	banUserToAPI,
	deleteMessageToAPI,
	pinMessageToAPI,
	sendMessageToAPI,
} from "../services";

const AGORA_APP_ID = process.env.REACT_APP_AGORA_APP_ID;

export const MessageType = {
	NORMAL: 0,
	MODERATION: 1,
	ADMIN: 2,
	PIN: 3,
	DELETE: 4,
} as const;
export type MessageType = (typeof MessageType)[keyof typeof MessageType];
export const ModerationReason = {
	BAN: 0,
	UNBAN: 1,
} as const;
export type ModerationReason =
	(typeof ModerationReason)[keyof typeof ModerationReason];

const createMessage = (
	msg: string,
	from: string,
	type: MessageType = MessageType.NORMAL,
	id?: string,
	time?: number
): TextMessage => {
	const itemId = id === undefined ? uuidv4() : id;
	const timeStamp = time === undefined ? new Date().getTime() : time;
	return {
		itemId,
		type,
		msg,
		from,
		time: timeStamp,
	};
};

const createPinMessage = (
	message: UserMessage,
	isAdmin: boolean
): PinMessage => {
	return { ...message, type: MessageType.PIN, isAdmin };
};

const createDeleteMessage = (message: UserMessage) => {
	return { ...message, type: MessageType.DELETE };
};

const createModMessage = (
	reason: ModerationReason,
	userId: string
): ModerationMessage => {
	return {
		itemId: uuidv4(),
		reason,
		userId,
		type: MessageType.MODERATION,
	};
};

interface UseAgoraSignalingType {
	eventStartDate: string;
	changeChatStatus: (currentStatus: ChatStatus) => void;
	processMessage: (message: UserMessage) => void;
}
export const useAgoraSignaling = ({
	eventStartDate,
	changeChatStatus,
	processMessage,
}: UseAgoraSignalingType) => {
	const { RTM } = AgoraRTM;
	const [channelId, setChannelId] = React.useState<string>("");
	const [parentId, setParentId] = React.useState<string>();
	const [currentUser, setCurrentUser] = React.useState<string>();
	const [bannedUsers, setbannedUsers] = React.useState<string[]>([]);
	const [engine, setEngine] = React.useState<RTMClient | null>(null);

	const _connectionStateChange = (newState: RTMEvents.LinkStateEvent) => {
		console.log(
			"FIRARTM LOGIN Connection state changed: ",
			newState.currentState
		);
		switch (newState.currentState) {
			case "CONNECTING":
				changeChatStatus(ChatStatus.connecting);
				break;
			case "CONNECTED":
				changeChatStatus(ChatStatus.connected);
				break;
			case "DISCONNECTED":
				changeChatStatus(ChatStatus.disconnected);
				setCurrentUser(undefined);
				setbannedUsers([]);
				disconnect();
				break;
			case "IDLE":
				changeChatStatus(ChatStatus.disconnected);
				setCurrentUser(undefined);
				setbannedUsers([]);
				disconnect();
				break;
			case "FAILED":
				changeChatStatus(ChatStatus.disconnected);
				setCurrentUser(undefined);
				setbannedUsers([]);
				disconnect();
				break;
			case "SUSPENDED":
				changeChatStatus(ChatStatus.disconnected);
				setCurrentUser(undefined);
				setbannedUsers([]);
				disconnect();
				break;
		}
	};

	const _onMessageReceived = (event: RTMEvents.MessageEvent) => {
		if (event.messageType === "STRING") {
			const parseMessage = JSON.parse(event.message as string) as Message;
			switch (parseMessage.type) {
				case MessageType.NORMAL:
					if (bannedUsers.find(value => value === event.publisher)) break;
					processMessage({
						...parseMessage,
						userId: event.publisher,
					} as UserMessage);
					break;
				case MessageType.ADMIN:
					processMessage({
						...parseMessage,
						userId: event.publisher,
					} as UserMessage);
					break;
				case MessageType.PIN:
					processMessage({
						...parseMessage,
						userId: event.publisher,
					} as UserMessage);
					break;
				case MessageType.MODERATION:
					processModMessage(parseMessage as ModerationMessage);
					break;
				case MessageType.DELETE:
					processMessage({
						...parseMessage,
						userId: event.publisher,
					} as UserMessage);
					break;
			}
		}
	};

	const connect = async (
		token: string,
		channelId: string,
		userId: string,
		parentId?: string
	) => {
		try {
			// init client
			disconnect();

			const newEngine = new RTM(AGORA_APP_ID as string, userId);
			newEngine.addEventListener("linkState", _connectionStateChange);
			newEngine.addEventListener("message", _onMessageReceived);

			await newEngine.login({ token });
			await newEngine.subscribe(channelId);

			setEngine(newEngine);
			setChannelId(channelId);
			setCurrentUser(userId);
			if (parentId) setParentId(parentId);
		} catch (error) {
			console.error("LOGIN CONNECT ERROR: ", error);
		}
	};

	const disconnect = async () => {
		try {
			setEngine(prevState => {
				if (prevState) {
					prevState.removeEventListener("linkState", _connectionStateChange);
					prevState.removeEventListener("message", _onMessageReceived);
					prevState.unsubscribe(channelId);
					prevState.logout();
				}
				return null;
			});
		} catch (error) {
			console.error("FIRARTM DISCONNECT ERROR: ", error);
		}
	};

	const processModMessage = (message: ModerationMessage) => {
		const { userId } = message;
		switch (message.reason) {
			case ModerationReason.BAN:
				setbannedUsers(prevState => [...prevState, userId]);
				break;
			case ModerationReason.UNBAN:
				setbannedUsers(prevState =>
					prevState.filter(value => value !== userId)
				);
				break;
		}
	};

	const sendMessage = async (
		msg: string,
		from: string,
		type: MessageType = MessageType.NORMAL
	) => {
		if (engine) {
			const message = createMessage(msg, from, type);
			if (bannedUsers.find(value => value === currentUser)) {
				processMessage({ ...message, userId: currentUser || "" });
				return true;
			}

			try {
				sendMessageToAPI(
					channelId,
					eventStartDate,
					type,
					{
						...message,
						userId: currentUser || "",
					},
					parentId
				);

				const stringMsg = JSON.stringify(message);
				await engine.publish(channelId, stringMsg);
				// processMessage({ ...message, userId: currentUser || "" });
				return true;
			} catch (error) {
				console.error("FIRARTM MESSAGE ERROR: ", error);
				return false;
			}
		}
		return false;
	};

	const sendPinMessage = async (message: UserMessage) => {
		if (engine) {
			try {
				const newPinMessage = createPinMessage(
					message,
					message.type === MessageType.ADMIN
				);
				pinMessageToAPI(
					channelId,
					{
						...message,
						userId: currentUser || "",
					},
					parentId
				);

				const stringMsg = JSON.stringify(newPinMessage);
				await engine.publish(channelId, stringMsg);

				processMessage({ ...newPinMessage, userId: message.userId || "" });
				return true;
			} catch (error) {
				return false;
			}
		}
		return false;
	};

	const sendDeleteMessage = async (message: UserMessage) => {
		if (engine) {
			try {
				const newDeleteMessage = createDeleteMessage(message);

				deleteMessageToAPI(channelId, newDeleteMessage, parentId);

				const stringMsg = JSON.stringify(newDeleteMessage);
				await engine.publish(channelId, stringMsg);

				processMessage({ ...newDeleteMessage });
				return true;
			} catch (error) {
				return false;
			}
		}
		return false;
	};

	const processBanSummary = (list: BanUserResponse[]) => {
		setbannedUsers(list.map(item => item.userId));
	};

	const banUser = async (userId: string) => {
		if (engine) {
			const message = createModMessage(ModerationReason.BAN, userId);
			const stringMsg = JSON.stringify(message);
			try {
				banUserToAPI(channelId, userId, parentId);

				await engine.publish(channelId, stringMsg);

				setbannedUsers(prevState => [...prevState, userId]);
			} catch (error) {
				console.error("FIRARTM MESSAGE ERROR: ", error);
			}
		}
	};

	const unbanUser = async (userId: string) => {
		// PROCESS UNBANNED USER ON THE BACKEND FIRST

		// -----------
		if (engine) {
			const message = createModMessage(ModerationReason.UNBAN, userId);
			const stringMsg = JSON.stringify(message);
			try {
				await engine.publish(channelId, stringMsg);

				setbannedUsers(prevState =>
					prevState.filter(value => value !== userId)
				);
			} catch (error) {
				console.error("FIRARTM MESSAGE ERROR: ", error);
			}
		}
	};

	return {
		connect,
		sendMessage,
		sendPinMessage,
		sendDeleteMessage,
		processBanSummary,
		banUser,
		unbanUser,
		bannedUsers,
	};
};
